import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ToolbarDrawerService } from '../toolbar-drawer.service';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../toolbar-control.tokens';
import {
  SearchState,
  VoyageSearchController,
  WorldwideRow,
} from '../voyage-search.controller';
import { VoyageLocalHit } from '../../voyage-radar/voyage-map.types';
import { voyageSpendBlocker } from '../../voyage-radar/voyage-credits.model';
import { BaseControl } from './base-control';

/**
 * Vessel search — the magnifier button and its drawer.
 *
 * Replaces `app-voyage-search`, the floating pill that sat top-centre over the
 * map. Same two-tier behaviour, and every rule it enforced is enforced here:
 *
 *  - FREE FILTERING as you type, over the drawn snapshot, any loaded frame and
 *    the task's kept vessels. Never a request, never a credit.
 *  - ONE PAID ROW widens the same query to the worldwide registry. It is a
 *    labelled button that must be pressed — never a keystroke, and Enter picks
 *    the highlighted result rather than firing it, so a stray return cannot
 *    spend.
 *  - RESULTS APPEND under their own heading. The paid block never replaces the
 *    free one; a user who bought a wider answer keeps the narrow one too.
 *  - LOCATING IS FREE on both lists. The search response carries a position, so
 *    unlike a nearby row there is nothing further to buy.
 *
 * What changed is the shell. The dropdown's own backdrop, its `focusout`
 * dismissal and its `mousedown`/`document:mouseup` press-tracking are all gone:
 * the CDK overlay behind `ToolbarDrawerService` owns dismissal now, and that is
 * the point of moving — one dismissal model for every panel on this map instead
 * of a bespoke one here. The `/` shortcut survives, because a search you cannot
 * reach from the keyboard is a search you stop using.
 */
@Component({
  selector: 'app-search-panel',
  template: `
    <!-- The box. Autofocused on open: the drawer was opened to type in, and a
         panel that needs a second click before it accepts a keystroke is a step
         the floating box never asked for. -->
    <div class="sp-box">
      <mat-icon class="material-icons-outlined sp-box__mag">search</mat-icon>
      <input #box
             class="sp-box__input"
             type="text"
             [placeholder]="placeholder"
             [value]="s.query"
             (input)="onInput($event)"
             (keydown)="onKeydown($event)"
             aria-label="Search vessels" />
      <button type="button" class="sp-box__clear" *ngIf="s.query"
              (click)="clear()" aria-label="Clear search">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- ══ Loaded map: the free list ══ -->
    <ng-container *ngIf="!s.mapEmpty">
      <div class="sp-sl">
        On the map
        <span class="sp-sl__n">{{ typed ? s.localHits.length : s.vesselCount }}</span>
      </div>

      <!-- Resting: teach the coverage before the first miss, not after. -->
      <div class="sp-quiet" *ngIf="!typed">
        Start typing to filter. {{ s.unnamed }} hulls here broadcast no name — those
        match by MMSI.
      </div>

      <ng-container *ngIf="typed">
        <div class="sp-list" *ngIf="s.localHits.length">
          <button type="button" class="sp-row"
                  *ngFor="let hit of s.localHits; let i = index"
                  [class.sp-row--sel]="cursor === i"
                  (mouseenter)="cursor = i"
                  (click)="pickLocal(hit)">
            <span class="sp-row__gl">▲</span>
            <span class="sp-row__main">
              <span class="sp-row__nm">{{ hit.name || 'MMSI ' + hit.mmsi }}</span>
              <span class="sp-row__meta">{{ hit.shipType ? hit.shipType + ' · ' : ''
                }}MMSI {{ hit.mmsi }}</span>
            </span>
            <span class="sp-row__tags">
              <span class="sp-tag sp-tag--kept" *ngIf="hit.kept">KEPT</span>
              <span class="sp-tag sp-tag--frame" *ngIf="frameLabel(hit) as f">{{ f }}</span>
            </span>
          </button>
        </div>

        <!-- The honest empty state: a miss is explained, never bare. -->
        <div class="sp-empty" *ngIf="!s.localHits.length && !s.worldwide.length">
          <div class="sp-empty__h">Nothing matching “{{ s.query.trim() }}” on the map.</div>
          <div class="sp-empty__s">
            {{ s.unnamed }} of {{ s.vesselCount }} hulls here broadcast no name — it
            could still be one of them. Try its MMSI.
          </div>
        </div>
      </ng-container>
    </ng-container>

    <!-- ══ Empty map. One quiet line, no sections: with nothing drawn the box
         has nothing to filter. Told apart on hasHistory only to name the right
         act — a task with saved refreshes LOADS one, a task with none has to
         pull first. ══ -->
    <ng-container *ngIf="s.mapEmpty">
      <!-- Kept vessels stay searchable with nothing drawn. -->
      <ng-container *ngIf="typed && s.localHits.length">
        <div class="sp-sl">Kept vessels <span class="sp-sl__n">{{ s.localHits.length }}</span></div>
        <div class="sp-list">
          <button type="button" class="sp-row"
                  *ngFor="let hit of s.localHits; let i = index"
                  [class.sp-row--sel]="cursor === i"
                  (mouseenter)="cursor = i"
                  (click)="pickLocal(hit)">
            <span class="sp-row__gl">◆</span>
            <span class="sp-row__main">
              <span class="sp-row__nm">{{ hit.name || 'MMSI ' + hit.mmsi }}</span>
              <span class="sp-row__meta">{{ hit.shipType ? hit.shipType + ' · ' : ''
                }}MMSI {{ hit.mmsi }}</span>
            </span>
            <span class="sp-row__tags"><span class="sp-tag sp-tag--kept">KEPT</span></span>
          </button>
        </div>
      </ng-container>

      <div class="sp-quiet" *ngIf="!typed || !s.localHits.length">
        <ng-container *ngIf="s.hasHistory; else neverRefreshed">
          Load a snapshot to search the vessels in it by name or MMSI.
        </ng-container>
        <ng-template #neverRefreshed>
          Refresh the sensor to pull vessels, then search them by name or MMSI.
        </ng-template>
      </div>
    </ng-container>

    <!-- ══ Worldwide block — appended, never replacing ══ -->
    <ng-container *ngIf="s.worldwide.length">
      <div class="sp-sl sp-sl--paid">
        Worldwide
        <span class="sp-sl__n">{{ s.mapEmpty || !s.localHits.length
          ? s.worldwide.length : s.worldwide.length + ' more' }}</span>
        <span class="sp-sl__r" *ngIf="s.worldwideTotal">top {{ s.worldwide.length }} of {{ s.worldwideTotal }}</span>
        <span class="sp-sl__r" *ngIf="!s.worldwideTotal">✓ searched</span>
      </div>
      <div class="sp-list">
        <button type="button" class="sp-row"
                *ngFor="let row of s.worldwide; let i = index"
                [class.sp-row--sel]="cursor === s.localHits.length + i"
                (mouseenter)="cursor = s.localHits.length + i"
                (click)="pickWorldwide(row)">
          <span class="sp-row__gl" [class.sp-row__gl--stale]="isStale(row)">{{
            isStale(row) ? '◇' : '◆' }}</span>
          <span class="sp-row__main">
            <span class="sp-row__nm">{{ rowName(row) }}</span>
            <span class="sp-row__meta">
              <b *ngIf="isStale(row)">Last known · </b>{{ rowMeta(row) }}
            </span>
          </span>
          <span class="sp-row__tags">
            <span class="sp-tag sp-tag--stale" *ngIf="isStale(row)">STALE</span>
            <span class="sp-row__ago">{{ age(row) }}</span>
          </span>
        </button>
      </div>
    </ng-container>

    <div class="sp-error" *ngIf="s.searchError">{{ s.searchError }}</div>

    <!-- ══ THE paid act. Present from the moment the drawer opens, not only
         once something has been typed: worldwide reach is the one thing here
         the user cannot discover by trying, and a button that appears
         mid-typing is a capability they had already decided this did not have.
         Untyped it is disabled and the cost line reads as an instruction —
         which is where the searchable FIELDS belong, because "no match" for an
         IMO the box never accepted is indistinguishable from "no such vessel".

         Hidden only once THIS query has been searched; the results standing
         above it under their own label already say so.

         A drawer primary at the foot of the body, like the trace, port-call
         and nearby drawers — this is the same act in the same place wearing
         the same clothes. ══ -->
    <ng-container *ngIf="!searched">
      <button type="button" class="tb-drawer__primary tb-drawer__primary--follows"
              [disabled]="s.searching || !!blocker || !typed"
              (click)="searchWorldwide()">
        <mat-icon class="material-icons-outlined" [class.sp-spin]="s.searching">public</mat-icon>
        <span class="tb-drawer__primary-label">{{ s.searching
          ? 'Searching worldwide…' : 'Search worldwide' }}</span>
        <em>{{ costLabel }}</em>
      </button>

      <div class="tb-drawer__cost">
        <ng-container *ngIf="blocker as why">
          {{ why }}
        </ng-container>
        <ng-container *ngIf="!blocker && !typed">
          Type a name, MMSI, IMO or call sign.
        </ng-container>
        <ng-container *ngIf="!blocker && typed">
          Matches name, MMSI, IMO or call sign — in or out of your target · Uses
          {{ costLabel }}<ng-container *ngIf="s.creditsRemaining != null">
            &middot; {{ s.creditsRemaining }} remaining</ng-container>
        </ng-container>
      </div>
    </ng-container>`,
  styles: [`
    /* Fixed width, for the reason the nearby panel is fixed: the drawer shrink-
       wraps between 260px and 360px, and this panel's content changes on every
       keystroke — rows appear, the widest vessel name changes, sections come
       and go. A card that resized while being typed into would be unusable. */
    :host {
      display: block;
      width: 332px;
      max-width: 100%;
    }

    .sp-box {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 5px 8px;
      margin-bottom: 4px;
      border: 1px solid rgba(128, 128, 128, 0.4);
      border-radius: 8px;
    }
    .sp-box:focus-within { border-color: #2563EB; }
    .sp-box__mag {
      flex: 0 0 auto;
      font-size: 16px;
      width: 16px;
      height: 16px;
      line-height: 16px;
      opacity: 0.6;
    }
    .sp-box__input {
      flex: 1 1 auto;
      min-width: 0;
      font: inherit;
      font-size: 12.5px;
      border: none;
      background: transparent;
      color: inherit;
      outline: none;
    }
    .sp-box__clear {
      flex: 0 0 auto;
      display: inline-flex;
      border: none;
      background: transparent;
      color: inherit;
      opacity: 0.55;
      cursor: pointer;
      padding: 0;
    }
    .sp-box__clear:hover { opacity: 1; }
    .sp-box__clear .mat-icon {
      font-size: 15px;
      width: 15px;
      height: 15px;
      line-height: 15px;
    }

    /* Section label — "On the map 12", "Worldwide 8 more". */
    .sp-sl {
      display: flex;
      align-items: baseline;
      gap: 6px;
      padding: 9px 2px 4px;
      font-size: 10.5px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      opacity: 0.6;
    }
    .sp-sl__n { font-weight: 600; font-variant-numeric: tabular-nums; opacity: 0.85; }
    .sp-sl__r { margin-left: auto; text-transform: none; letter-spacing: 0; }
    .sp-sl--paid { color: #2563EB; opacity: 0.9; }

    .sp-quiet {
      font-size: 11.5px;
      opacity: 0.7;
      line-height: 1.45;
      padding: 2px 2px 6px;
    }

    /* Each section scrolls on its own rather than the whole body, so the paid
       button at the foot stays reachable without scrolling past 20 results. */
    .sp-list {
      display: flex;
      flex-direction: column;
      max-height: 208px;
      overflow-y: auto;
    }

    /* The row IS the button, as everywhere else on this map. */
    .sp-row {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 6px 4px;
      font: inherit;
      text-align: left;
      border: none;
      background: transparent;
      color: inherit;
      cursor: pointer;
      border-radius: 6px;
    }
    .sp-row + .sp-row { border-top: 1px solid rgba(128, 128, 128, 0.18); }
    .sp-row--sel, .sp-row:hover { background: rgba(37, 99, 235, 0.08); }
    .sp-row:focus-visible { outline: 2px solid #2563EB; outline-offset: -2px; }
    .sp-row__gl { flex: 0 0 auto; font-size: 10px; opacity: 0.55; width: 10px; }
    .sp-row__gl--stale { opacity: 0.4; }
    .sp-row__main {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      line-height: 1.25;
    }
    .sp-row__nm {
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .sp-row__meta {
      font-size: 10.5px;
      opacity: 0.65;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .sp-row__tags {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      gap: 4px;
      white-space: nowrap;
    }
    .sp-row__ago { font-size: 10px; opacity: 0.55; font-variant-numeric: tabular-nums; }
    .sp-tag {
      font-size: 9px;
      letter-spacing: 0.04em;
      padding: 1px 5px;
      border-radius: 999px;
      border: 1px solid rgba(128, 128, 128, 0.4);
      opacity: 0.75;
    }
    .sp-tag--kept { border-color: rgba(22, 163, 74, 0.5); color: #15803d; }
    .sp-tag--stale { border-color: rgba(217, 119, 6, 0.5); color: #b45309; }
    .sp-tag--frame { font-variant-numeric: tabular-nums; }

    .sp-empty { padding: 6px 2px 8px; line-height: 1.45; }
    .sp-empty__h { font-size: 11.5px; font-weight: 600; }
    .sp-empty__s { font-size: 11px; opacity: 0.7; margin-top: 2px; }

    .sp-error {
      margin-top: 8px;
      padding: 7px 8px;
      border-radius: 6px;
      background: rgba(217, 119, 6, 0.12);
      font-size: 11px;
      line-height: 1.45;
    }

    .sp-spin { animation: sp-spin 1.1s linear infinite; }
    @keyframes sp-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) { .sp-spin { animation: none; } }

    /* :host-context, NOT a bare .dark-theme descendant selector: the theme class
       sits on an ancestor outside this component, and this panel renders into a
       CDK overlay further from the host still. */
    :host-context(.dark-theme) .sp-box,
    :host-context(.dark-theme) .sp-tag {
      border-color: rgba(255, 255, 255, 0.28);
    }
    :host-context(.dark-theme) .sp-row--sel,
    :host-context(.dark-theme) .sp-row:hover {
      background: rgba(96, 165, 250, 0.14);
    }
    :host-context(.dark-theme) .sp-tag--kept { color: #4ade80; }
    :host-context(.dark-theme) .sp-tag--stale { color: #fbbf24; }
  `],
})
export class SearchPanelComponent implements AfterViewInit {
  @Input() s!: SearchState;

  @ViewChild('box') box?: ElementRef<HTMLInputElement>;

  /** Keyboard cursor over the combined (local + worldwide) row list. */
  cursor = -1;

  constructor(private controller: VoyageSearchController) {}

  /**
   * The drawer was opened to type in. Panel-local rather than controller state
   * because a fresh instance is built on every open, which is exactly when the
   * focus should land.
   */
  ngAfterViewInit(): void {
    this.box?.nativeElement.focus();
  }

  get typed(): boolean {
    return this.controller.typed;
  }

  get searched(): boolean {
    return this.controller.searched;
  }

  /**
   * What this button must go DEAD for, or null when it stays live — an empty
   * wallet, and deliberately NOT an ended TOI, which stays pressable so the
   * press can be answered with the modal every other paid control raises. See
   * `voyageSpendBlocker`. Unknown balance counts as affordable; the server's
   * 402 is the referee.
   */
  get blocker(): string | null {
    return voyageSpendBlocker({
      action: 'search',
      cost: this.s.searchCredits,
      remaining: this.s.creditsRemaining,
      toiEnded: this.s.toiEnded,
    });
  }

  get costLabel(): string {
    const n = this.s.searchCredits;
    return `${n} ${n === 1 ? 'credit' : 'credits'}`;
  }

  /**
   * Deliberately plain with nothing listed: the note under the box says what
   * filtering needs, and the paid button's cost line states the fields IT
   * matches, so a placeholder naming either would be the third voice on one
   * question.
   */
  get placeholder(): string {
    return this.s.mapEmpty ? 'Find a vessel…' : 'Search name or MMSI…';
  }

  onInput(ev: Event): void {
    this.cursor = -1;
    this.controller.setQuery((ev.target as HTMLInputElement).value);
  }

  clear(): void {
    this.controller.clear();
    this.cursor = -1;
    this.box?.nativeElement.focus();
  }

  /**
   * Escape is handled HERE and stopped, so it does not reach the overlay.
   *
   * The drawer closes on Escape like every CDK panel, and that is right when
   * the drawer is a list. With a text box in it the key has an earlier meaning:
   * clear what I typed. So a non-empty query consumes it and a second press —
   * with the box now empty — falls through and closes the drawer, which is the
   * order a search box has always used.
   */
  onKeydown(ev: KeyboardEvent): void {
    const rows = this.s.localHits.length + this.s.worldwide.length;

    if (ev.key === 'Escape') {
      if (this.s.query) {
        ev.preventDefault();
        ev.stopPropagation();
        this.clear();
      }
      return;
    }

    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      if (!rows) return;
      const step = ev.key === 'ArrowDown' ? 1 : -1;
      this.cursor = (this.cursor + step + rows) % rows;
      return;
    }

    // Enter picks the highlighted row. It NEVER fires the paid search — that
    // takes the labelled button, so a stray keystroke cannot spend.
    if (ev.key === 'Enter') {
      ev.preventDefault();
      if (this.cursor < 0 && rows > 0) this.cursor = 0;
      if (this.cursor < 0) return;
      if (this.cursor < this.s.localHits.length) {
        this.pickLocal(this.s.localHits[this.cursor]);
      } else {
        this.pickWorldwide(this.s.worldwide[this.cursor - this.s.localHits.length]);
      }
    }
  }

  pickLocal(hit: VoyageLocalHit): void {
    this.controller.pickLocal(hit);
  }

  pickWorldwide(row: WorldwideRow): void {
    this.controller.pickWorldwide(row);
  }

  searchWorldwide(): void {
    this.controller.searchWorldwide();
  }

  rowName(row: WorldwideRow): string {
    return row.shipName || `MMSI ${row.id}`;
  }

  rowMeta(row: WorldwideRow): string {
    return [row.shipType, row.imoNumber ? `IMO ${row.imoNumber}` : null, row.waterBody]
      .filter(Boolean)
      .join(' · ');
  }

  /** >48 h — captioned "last known", hollow glyph, never "latest". */
  isStale(row: WorldwideRow): boolean {
    const t = new Date(row.time_utc ?? '').getTime();
    return isNaN(t) || Date.now() - t > 48 * 3600 * 1000;
  }

  age(row: WorldwideRow): string | null {
    const t = new Date(row.time_utc ?? '').getTime();
    if (isNaN(t)) return null;
    const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
    if (mins < 60) return `${mins} min ago`;
    if (mins < 2880) return `${Math.round(mins / 60)} h ago`;
    return `${Math.round(mins / 1440)} d ago`;
  }

  frameLabel(hit: VoyageLocalHit): string | null {
    if (hit.source !== 'frame' || !hit.frameTime) return null;
    const d = new Date(hit.frameTime);
    if (isNaN(d.getTime())) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  }
}

@Component({
  selector: 'app-search-control',
  template: `
    <!-- Standing, like every other Voyage Radar control: search is the entry
         point to the whole worldwide workflow, and one that only appeared under
         some condition would be the hardest thing here to discover. -->
    <ng-container *ngIf="search.state$ | async as s">
      <button #trigger type="button" class="tb-btn"
              [class.tb-btn--active]="drawer.isOpen(panel)"
              (click)="toggle(trigger)"
              aria-label="Search vessels"
              [matTooltip]="'Search vessels · / · worldwide costs ' + costLabel(s)"
              matTooltipPosition="above">
        <mat-icon class="material-icons-outlined">search</mat-icon>
      </button>
    </ng-container>

    <ng-template #panel>
      <app-toolbar-drawer *ngIf="search.state$ | async as s"
                          title="Vessel search"
                          [subtitle]="subtitle(s)"
                          (close)="drawer.close()">
        <app-search-panel [s]="s"></app-search-panel>
      </app-toolbar-drawer>
    </ng-template>`,
})
export class SearchControlComponent extends BaseControl implements AfterViewInit, OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;
  @ViewChild('trigger') triggerRef!: ElementRef;

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    public search: VoyageSearchController,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
  ) {
    super();
  }

  private _openFn = () => {
    this.drawer.open(this.panel, this.vcr, this.triggerRef);
  };

  ngAfterViewInit(): void {
    this.search.onRequestOpen = this._openFn;
  }

  /**
   * Re-filters the free list before showing it. The map may have been
   * refreshed, swapped or cleared since the last open, and a hit list held over
   * that would offer vessels that are no longer drawn.
   */
  toggle(trigger: HTMLElement): void {
    if (!this.drawer.isOpen(this.panel)) this.search.refreshLocalHits();
    this.drawer.toggle(this.panel, this.vcr, trigger);
  }

  /**
   * `/` opens the drawer and focuses the box — and does nothing when focus is
   * already in an input, textarea or contenteditable, where the user is typing
   * a literal slash. Carried over from the floating box: a search reachable
   * only by pointer is one that stops being used.
   *
   * On the document rather than the host because the point is to work when this
   * button does NOT have focus, which is almost always.
   */
  @HostListener('document:keydown', ['$event'])
  onDocKeydown(ev: KeyboardEvent): void {
    if (ev.key !== '/' || ev.ctrlKey || ev.metaKey || ev.altKey) return;
    const t = ev.target as HTMLElement | null;
    const tag = t?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return;
    if (this.drawer.isOpen(this.panel)) return;
    ev.preventDefault();
    this.search.openAndFocus();
  }

  costLabel(s: SearchState): string {
    return `${s.searchCredits} ${s.searchCredits === 1 ? 'credit' : 'credits'}`;
  }

  /**
   * The price, always — unlike the per-vessel drawers this one has no subject to
   * name instead, and the paid button is below the fold whenever a query has
   * returned a long list.
   */
  subtitle(s: SearchState): string {
    return `Free on this map · ${this.costLabel(s)} worldwide`;
  }

  ngOnDestroy(): void {
    this.drawer.closeIfOpen(this.panel);
    // Only if THIS instance owns it: a slot swap tears one down and stands
    // another up, and the new one's ngAfterViewInit must win.
    if (this.search.onRequestOpen === this._openFn) this.search.onRequestOpen = null;
  }
}
